import type { StreamKey } from './types';

type Listener = (event: MessageEvent) => void;
type ErrorListener = ((event: Event) => void) | null;

type RecordSink = (stream: StreamKey, type: string, data: string) => void;

export class RecordingEventSource {
  private readonly real: EventSource;
  private readonly teed = new Set<string>();
  private handleMessage: Listener | null = null;

  constructor(
    url: string,
    init: EventSourceInit | undefined,
    private readonly stream: StreamKey,
    private readonly sink: RecordSink,
  ) {
    this.real = new EventSource(url, init);
    this.real.onmessage = (event: MessageEvent) => {
      this.sink(this.stream, 'message', event.data as string);
      this.handleMessage?.(event);
    };
  }

  get url(): string { return this.real.url; }
  get readyState(): number { return this.real.readyState; }
  get withCredentials(): boolean { return this.real.withCredentials; }

  get onmessage(): Listener | null { return this.handleMessage; }
  set onmessage(listener: Listener | null) { this.handleMessage = listener; }

  get onerror(): ErrorListener { return this.real.onerror as ErrorListener; }
  set onerror(listener: ErrorListener) { this.real.onerror = listener as EventSource['onerror']; }

  get onopen(): ErrorListener { return this.real.onopen as ErrorListener; }
  set onopen(listener: ErrorListener) { this.real.onopen = listener as EventSource['onopen']; }

  addEventListener(type: string, listener: EventListener): void {
    if (!this.teed.has(type)) {
      this.teed.add(type);
      this.real.addEventListener(type, ((event: MessageEvent) => {
        this.sink(this.stream, type, event.data as string);
      }) as EventListener);
    }
    this.real.addEventListener(type, listener);
  }

  removeEventListener(type: string, listener: EventListener): void {
    this.real.removeEventListener(type, listener);
  }

  close(): void {
    this.real.close();
  }
}
